import {
    mdiDotsHorizontal,
    mdiFileDocumentOutline,
    mdiGavel,
    mdiSendOutline,
    mdiUndoVariant,
} from '@mdi/js';
import { Address, ClearinghouseTransactionType, PayerContact, SupportClearinghousePayer } from '../../api';

/**
 * Where each kind of data goes for a payer — electronic via a clearinghouse, or on paper.
 *
 * ## This is derived, not stored
 *
 * Nothing on `SupportPayer` says "claims drop to paper". It follows from two things the API
 * does tell us: which X12 transactions the payer's clearinghouse connection supports
 * (`SupportClearinghousePayer.transactions`), and where the payer wants each kind of
 * correspondence sent (`PayerContact`, five purposes × four channels).
 *
 * ## Why reconsiderations are always paper
 *
 * The X12 set the platform models is 837P/I/D (claims), 270/271 (eligibility), 276/277
 * (status), 278 (authorisation), 834 (enrolment), 835 (remittance) and 275 (attachments).
 * **There is no transaction for an appeal.** So a reconsideration cannot go electronically
 * however well connected the payer is — which is exactly what the design says, and it is a
 * property of X12 rather than of any one payer's setup.
 *
 * Claim data rides 837; paperwork rides 275 where the payer supports it.
 *
 * ## The state that matters operationally
 *
 * `paper-unconfigured` is the one worth acting on: this data kind can only go on paper, and
 * the payer has no fax or address recorded for it. Appeals in that state cannot be sent at
 * all, and nothing else in the product says so. It is deliberately distinct from `unknown`,
 * which only means the clearinghouse join has not resolved yet — asserting "unconfigured"
 * before we have looked would send someone to fix a payer that is fine.
 */

export type PayerDataKind = 'claim' | 'reconsideration' | 'paperwork' | 'overpayment' | 'other';

export type PayerRoute =
    /**
     * One or more clearinghouses can carry this data kind. `via` lists every one of them:
     * which the platform actually picks is the backend's decision, so naming a single
     * clearinghouse would be asserting a routing fact we do not have.
     */
    | { channel: 'electronic'; via: string[]; transactions: ClearinghouseTransactionType[] }
    | { channel: 'paper'; via: 'Fax' | 'Mail'; destination: string }
    | { channel: 'paper-unconfigured' }
    | { channel: 'unknown' };

/** X12 transactions that can carry each data kind. Appeals have none — see above. */
const ELECTRONIC_TRANSACTIONS: Record<PayerDataKind, ClearinghouseTransactionType[]> = {
    claim: [
        ClearinghouseTransactionType.EDI837P,
        ClearinghouseTransactionType.EDI837I,
        ClearinghouseTransactionType.EDI837D,
    ],
    paperwork: [ClearinghouseTransactionType.EDI275],
    // No X12 transaction carries an appeal, an overpayment or refund exchange, or general
    // correspondence, so all three are paper by construction.
    reconsideration: [],
    overpayment: [],
    other: [],
};

/** Which `PayerContact` purpose each data kind corresponds to. */
const CONTACT_SUFFIX: Record<PayerDataKind, 'Claim' | 'Appeal' | 'Documentation' | 'Overpayment' | 'Other'> = {
    claim: 'Claim',
    reconsideration: 'Appeal',
    paperwork: 'Documentation',
    overpayment: 'Overpayment',
    other: 'Other',
};

/** Column wording, per the design's Payers list. */
export const DATA_KIND_LABEL: Record<PayerDataKind, string> = {
    claim: 'Claim data',
    reconsideration: 'Reconsiderations',
    paperwork: 'Paperwork',
    overpayment: 'Overpayments',
    other: 'Other',
};

/** Detail-panel wording, per the design's drop-to-paper paths table. */
export const DATA_KIND_PATH_LABEL: Record<PayerDataKind, string> = {
    claim: 'Claim submissions',
    reconsideration: 'Reconsiderations',
    paperwork: 'Medical records and supporting documentation',
    overpayment: 'Overpayments and refunds',
    other: 'Other',
};

/**
 * The list summarises three kinds; the detail panel shows every path, matching the design.
 * All five correspond one-to-one with PayerContact's purposes.
 */
/**
 * The design's icon per data kind, shared so the editor, both expanded rows and the drawer
 * cannot drift apart on it.
 *
 * Distinct from the CHANNEL icon (envelope / fax / mailbox), which says how something leaves.
 * This says what kind of data it is. The read-only surfaces show the channel where a path is
 * configured, and the editor shows the category — the design uses both, for different jobs.
 */
export const DATA_KIND_ICON: Record<PayerDataKind, string> = {
    claim: mdiSendOutline,
    reconsideration: mdiGavel,
    paperwork: mdiFileDocumentOutline,
    overpayment: mdiUndoVariant,
    other: mdiDotsHorizontal,
};

export const LIST_DATA_KINDS: PayerDataKind[] = ['claim', 'reconsideration', 'paperwork'];
export const DATA_KINDS: PayerDataKind[] = ['claim', 'reconsideration', 'paperwork', 'overpayment', 'other'];

function formatAddress(address: Address): string {
    return [address.name, address.city, address.state].filter(Boolean).join(', ');
}

/**
 * Fax before mail: both are paper, but a fax number is the one an operator can act on
 * immediately, and it is how claims correspondence actually leaves this platform.
 */
function paperRoute(contact: PayerContact | undefined, kind: PayerDataKind): PayerRoute {
    const suffix = CONTACT_SUFFIX[kind];
    const fax = contact?.[`fax${suffix}` as keyof PayerContact] as string | undefined;

    if (fax) {
        return { channel: 'paper', via: 'Fax', destination: fax };
    }

    const address = contact?.[`address${suffix}` as keyof PayerContact] as Address | undefined;

    if (address) {
        return { channel: 'paper', via: 'Mail', destination: formatAddress(address) };
    }

    return { channel: 'paper-unconfigured' };
}

/**
 * @param bindings the payer's clearinghouse connections, or `undefined` while the join is
 *                 still in flight. `undefined` yields `unknown`, never a negative.
 */
export function getPayerRoute(
    kind: PayerDataKind,
    contact: PayerContact | undefined,
    bindings: SupportClearinghousePayer[] | undefined,
): PayerRoute {
    const electronic = ELECTRONIC_TRANSACTIONS[kind];

    // No X12 transaction can carry it, so the clearinghouse is irrelevant and we do not need
    // the join to answer — reconsiderations resolve even while bindings are loading.
    if (electronic.length === 0) {
        return paperRoute(contact, kind);
    }

    if (!bindings) {
        return { channel: 'unknown' };
    }

    // Every binding that can carry this kind, not just the first. Returning the first match
    // made the answer depend on response order — the same payer could name a different
    // clearinghouse between loads — and it disagreed with the Transactions column, which
    // unions across all bindings. A payer with two connections legitimately has two routes.
    const carriers: string[] = [];
    const supported = new Set<ClearinghouseTransactionType>();

    for (const binding of bindings) {
        const matched = (binding.transactions || []).filter(t => electronic.includes(t));

        if (matched.length > 0) {
            carriers.push(binding.clearinghouse?.name || 'Clearinghouse');
            matched.forEach(t => supported.add(t));
        }
    }

    if (carriers.length > 0) {
        return {
            channel: 'electronic',
            via: Array.from(new Set(carriers)),
            transactions: Array.from(supported),
        };
    }

    return paperRoute(contact, kind);
}

/** True when every data kind falls back to paper — the design's "ALWAYS PAPER" state. */
export function isAlwaysPaper(
    contact: PayerContact | undefined,
    bindings: SupportClearinghousePayer[] | undefined,
): boolean {
    if (!bindings) {
        return false;
    }

    return DATA_KINDS.every(kind => getPayerRoute(kind, contact, bindings).channel !== 'electronic');
}
